/**
 * Estilos da barra de filtros das abas e listas (RN-539b).
 *
 * Existiam em cada tela uma cópia levemente diferente — campo de data ora com 140px ora com
 * 150px, select ora com 170px ora sem mínimo — e a diferença aparecia ao alternar de aba:
 * os campos mudavam de largura e a barra "pulava". Aqui é a fonte única; tela nova importa,
 * não recria.
 *
 * A régua das larguras: todo campo que não é data cresce por igual e divide o espaço da linha
 * entre si. A aba que só tinha datas ficava com a barra pela metade — resolvido dando a ela
 * filtros próprios (marco e especialidade), não esticando as datas.
 *
 * As datas são a exceção: largura fixa, porque o conteúdo tem tamanho fixo. Ficam por último
 * na barra, e são os campos que crescem à esquerda delas que preenchem a linha.
 *
 * Responsivo: `flex-basis` menor que a tela do celular + `flex-wrap` na barra fazem os campos
 * quebrarem de linha e ocuparem a largura toda, um por linha, sem rolagem horizontal. Os
 * botões têm `flexShrink: 0` para nunca serem espremidos.
 */

import { colors, inputs, typography } from './designSystem';

const baseField = {
  backgroundColor: colors.white,
  '& .MuiOutlinedInput-root': {
    // Tamanho do campo vem do design system (1rem), não de uma medida própria: os 9
    // relatórios já usavam 1rem e este arquivo tinha nascido com 0,875rem, criando uma
    // segunda medida de campo no sistema.
    fontSize: inputs.default.fontSize,
    height: inputs.default.height,
    '& fieldset': { borderColor: colors.border },
    '&:hover fieldset': { borderColor: colors.border },
    '&.Mui-focused fieldset': { borderColor: colors.primary },
  },
};

/** Select e campos curtos (status, tipo, profissional). */
export const filterFieldSx = {
  ...baseField,
  // No celular ocupa a linha inteira: com `minWidth` cabiam dois por linha e o último
  // sobrava sozinho e esticado, com largura diferente dos de cima (achado do Hilton, 02/09).
  flex: { xs: '1 1 100%', sm: '1 1 170px' },
  minWidth: { xs: '100%', sm: 150 },
};

/**
 * Busca por texto livre. Mesma largura dos selects (decisão do Hilton, 02/09): dentro de uma
 * aba, todo campo que não é data divide o espaço por igual — a busca puxava o dobro e a barra
 * ficava desequilibrada de aba para aba, conforme cada uma tivesse busca ou não.
 */
export const searchFieldSx = {
  ...baseField,
  flex: { xs: '1 1 100%', sm: '1 1 170px' },
  minWidth: { xs: '100%', sm: 150 },
};

/**
 * Data (De/Até): largura FIXA (decisão do Hilton, 02/09) — o conteúdo é sempre dd/mm/aaaa,
 * então esticar só afasta os campos entre si. Vêm sempre por último na barra, depois dos
 * campos que crescem e antes dos botões: assim ficam ancoradas no mesmo ponto em toda aba.
 * No celular ocupam a linha inteira, como os demais.
 */
export const dateFieldSx = {
  ...baseField,
  flex: { xs: '1 1 100%', sm: '0 0 150px' },
  width: { xs: '100%', sm: 150 },
};

/** Botão cinza de limpar filtros, no fim da barra. */
export const clearFilterButtonSx = {
  bgcolor: colors.backgroundAlt,
  color: colors.textSecondary,
  width: 40,
  height: 40,
  // QUADRADO de canto suave (8px), como o `.icon-btn` do design system. Sem isto o
  // `IconButton` do MUI sai redondo, que é o padrão dele (achado do Hilton, 02/09).
  borderRadius: '8px',
  flexShrink: 0,
  '&:hover': { bgcolor: colors.backgroundAlt },
  '&.Mui-disabled': { bgcolor: colors.backgroundAlt, color: colors.borderSubtle },
};

/** Ação principal da aba (contornado 40x40), à direita da barra de filtros. */
export const filterActionButtonSx = {
  color: colors.primary,
  border: `2px solid ${colors.primary}`,
  borderRadius: '8px',
  width: 40,
  height: 40,
  flexShrink: 0,
  '&:hover': { borderColor: colors.primaryHover, backgroundColor: 'rgba(3, 180, 198, 0.08)' },
  '&.Mui-disabled': { color: colors.borderSubtle, borderColor: colors.borderSubtle },
};

/**
 * Botão de incluir das TELAS (verde preenchido). Regra do Hilton, 02/09/2026: verde é o "+"
 * de página; o contornado (`filterActionButtonSx`) é o de **seção interna** — abas do cadastro
 * e blocos dentro de uma tela. Misturar os dois faz o mesmo gesto mudar de cor sem motivo.
 */
export const addButtonSx = {
  backgroundColor: colors.add,
  color: colors.white,
  width: 40,
  height: 40,
  borderRadius: '8px',
  flexShrink: 0,
  '&:hover': { backgroundColor: colors.addHover },
};

/**
 * Contagem do resultado ("468 pacientes encontrados"), entre os filtros e a lista.
 *
 * Existe como token porque cada tela vinha escolhendo a própria margem no olho — `mb: 2` numa,
 * `mt: 2, mb: 1.5` noutra — e os blocos ficavam em alturas diferentes de tela para tela
 * (achado do Hilton, 02/09/2026). Aqui os três espaços da página têm um valor só.
 */
export const resultCountSx = {
  marginTop: '1rem',
  marginBottom: '0.75rem',
  color: colors.textSecondary,
  fontSize: typography.fontSize.sm,
};

/**
 * Cabeçalho de lista — a régua única (achado do Hilton, 02/09/2026: "os títulos das tabelas
 * estão diferentes na fonte, cor e altura da linha").
 *
 * Os valores já eram os mesmos no papel, mas cada tela os escrevia à mão e a altura acabava
 * divergindo: a tabela do MUI calcula pela `line-height` da célula e as listas em flexbox,
 * pelo padding. Aqui a altura é fixa, então as duas fecham no mesmo pixel.
 */
export const listHeaderSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  width: '100%',
  boxSizing: 'border-box' as const,
  minHeight: 48,
  padding: '0 1rem',
  backgroundColor: colors.backgroundAlt,
  fontWeight: typography.fontWeight.semibold,
  fontSize: typography.fontSize.sm,
  color: colors.textPrimary,
};

/** Linha da lista: mesmo alinhamento e colunas do cabeçalho. */
export const listRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  width: '100%',
  boxSizing: 'border-box' as const,
};

/**
 * Contêiner branco que envolve os filtros (decisão do Hilton, 02/09/2026). O filtro pertence
 * à lista que ele recorta: separá-los em blocos visuais diferentes sugere assuntos
 * independentes. Mesmos valores da Agenda e da Lista de pacientes, que já o usavam.
 */
export const filterContainerSx = {
  backgroundColor: colors.white,
  borderRadius: '12px',
  padding: '1.5rem',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
  border: '1px solid #e9ecef',
  // Sem margem inferior própria: quem separa da contagem é `resultCountSx`. Ter as duas
  // somava espaços diferentes conforme a tela usasse uma, outra ou as duas.
};

/** A barra em si: quebra em várias linhas no celular, sem rolagem horizontal. */
export const filterBarSx = {
  display: 'flex',
  gap: 1.5,
  alignItems: 'center',
  flexWrap: 'wrap',
  mb: 2,
};
